/**
 * Fusion AI conversation: messages (safe markdown, "Looked at" chips, file chips), action cards the person confirms,
 * a "Thinking… 12s" bubble while the model works, and the composer. Used by the floating panel and the /assistant page.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import clsx from "clsx";
import { Check, ExternalLink, FileText, Loader2, Paperclip, Send, Sparkles, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { api, ApiError } from "../../lib/api";
import { Markdown } from "../Markdown";
import { AK, call, errText, lookedAt, MAX_FILE_BYTES, MAX_FILES, readFile, refreshAfterAction, RHYTHM_LABEL, type AsAction, type AsApplied, type AsMessage, type AsThread } from "./api";

const STARTERS = ["What needs me today?", "What am I waiting on from others?", "Plan my afternoon", "Catch me up on my mail"];

/** Counts seconds while `on` is true. */
export function useSeconds(on: boolean) {
  const [s, setS] = useState(0);
  useEffect(() => {
    if (!on) return;
    setS(0);
    const t = setInterval(() => setS((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [on]);
  return s;
}

export function ThinkingBubble({ label = "Thinking…", seconds }: { label?: string; seconds: number }) {
  return (
    <div className="my-2.5 flex" role="status" aria-live="polite">
      <div className="muted rounded-xl bg-[var(--panel-2)] px-3 py-2 text-[0.84rem] ring-1 ring-[var(--line)]">
        <Loader2 size={13} className="mr-1 inline animate-spin" />
        {label}
        {seconds >= 2 && <span className="num"> {seconds}s</span>}
      </div>
    </div>
  );
}

export function useThread(id: string | null) {
  return useQuery({
    queryKey: AK.thread(id ?? "none"),
    queryFn: () => api<AsThread>(`/api/assistant/conversations/${id}`),
    enabled: !!id,
    retry: (n, e) => !(e instanceof ApiError && e.status === 404) && n < 2,
    // a long answer is still being written in the background: look again until it lands
    refetchInterval: (q) => (q.state.data?.messages.some((m) => m.meta?.pending) ? 4000 : false),
  });
}

export function Conversation({ id, projectId, onCreated, onMissing, compact, busyLabel, busySeconds }: {
  id: string | null;
  /** Project for a conversation created by the first message. */
  projectId?: string | null;
  onCreated: (id: string) => void;
  /** The conversation was deleted elsewhere. */
  onMissing?: () => void;
  compact?: boolean;
  /** Outside work on this conversation (e.g. minutes) shown as a thinking bubble. */
  busyLabel?: string | null;
  busySeconds?: number;
}) {
  const qc = useQueryClient();
  const thread = useThread(id);
  const [input, setInput] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [pending, setPending] = useState<{ convId: string; text: string; files: string[] } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const seconds = useSeconds(busy);
  const endRef = useRef<HTMLDivElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const missing = thread.error instanceof ApiError && thread.error.status === 404;
  useEffect(() => { if (missing) onMissing?.(); }, [missing, onMissing]);
  useEffect(() => { setErr(""); }, [id]);

  const messages = thread.data?.messages ?? [];
  const showPending = pending && (pending.convId === id || !id);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [messages.length, showPending, busyLabel]);

  const addFiles = (list: FileList | null) => {
    if (!list?.length) return;
    const picked = [...files, ...list];
    const big = picked.find((f) => f.size > MAX_FILE_BYTES);
    if (big) { setErr(`${big.name} is larger than 10 MB.`); return; }
    if (picked.length > MAX_FILES) setErr(`Up to ${MAX_FILES} files per message.`);
    else setErr("");
    setFiles(picked.slice(0, MAX_FILES));
  };

  const send = async (override?: string) => {
    const text = (override ?? input).trim();
    if ((!text && !files.length) || busy) return;
    setErr("");
    setBusy(true);
    const sentText = text || "Please read what I attached and tell me what matters.";
    const sentFiles = files;
    let cid = id;
    let sent = false;
    let created: string | null = null; // reported after the reply, so a route change can't interrupt the send
    try {
      const payload = await Promise.all(sentFiles.map(readFile));
      if (!cid) {
        const r = await call<{ conversation: { id: string } }>("POST", "/api/assistant/conversations", projectId ? { projectId } : {});
        if (!r.ok || !r.body?.conversation) throw new Error(errText(r.body, r.status));
        cid = r.body.conversation.id;
        created = cid;
      }
      setInput(""); setFiles([]);
      if (taRef.current) taRef.current.style.height = "auto";
      setPending({ convId: cid, text: sentText, files: sentFiles.map((f) => f.name) });
      sent = true;
      const r = await call<{ message: AsMessage | null; error?: string }>("POST", `/api/assistant/conversations/${cid}/messages`, { text: sentText, ...(payload.length ? { files: payload } : {}) });
      if (!r.ok || !r.body?.message) setErr(errText(r.body, r.status));
    } catch (e) {
      setErr((e as Error).message || "Could not send");
      if (!sent) setInput(text);
    }
    // the question is saved even when the model failed: reload the thread and the titles
    if (cid) await Promise.all([qc.invalidateQueries({ queryKey: AK.thread(cid) }), qc.invalidateQueries({ queryKey: AK.state })]);
    if (created) {
      const cidNew = created;
      await qc.prefetchQuery({ queryKey: AK.thread(cidNew), queryFn: () => api<AsThread>(`/api/assistant/conversations/${cidNew}`) });
      onCreated(cidNew);
    }
    setPending(null);
    setBusy(false);
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className={clsx("scroll-thin min-h-0 flex-1 overflow-y-auto", compact ? "px-3 py-2" : "px-1 py-2 pr-2")}>
        {id && thread.isLoading && <p className="muted py-6 text-center text-sm"><Loader2 size={13} className="mr-1 inline animate-spin" /> Opening the conversation…</p>}
        {thread.error && !missing && <p className="py-4 text-sm text-red-600">{(thread.error as Error).message}</p>}
        {!messages.length && !showPending && !thread.isLoading && (
          <div className="mx-auto my-8 max-w-[460px] text-center text-sm text-[var(--text-2)]">
            <span className="mx-auto mb-2 grid h-10 w-10 place-items-center rounded-xl bg-brand-50 text-brand-600"><Sparkles size={18} /></span>
            <h3 className="mb-1 text-[16px] font-bold text-[var(--text)]">Fusion AI</h3>
            <p>Ask about your day, your tasks, notes, calendar or mail. When something needs doing I prepare it and you confirm.</p>
            <div className="mt-3 flex flex-wrap justify-center gap-1.5">
              {STARTERS.map((s) => <button key={s} type="button" className="chip cursor-pointer whitespace-normal hover:border-brand-200" onClick={() => void send(s)}>{s}</button>)}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <MessageView key={m.id} m={m} quickReplies={i === messages.length - 1 && !busy && !showPending ? m.meta?.quickReplies : undefined} onQuickReply={(t) => void send(t)} />
        ))}
        {showPending && (
          <div className="my-2.5 flex flex-col items-end">
            <FileChips names={pending.files} />
            <div className="max-w-[92%] whitespace-pre-wrap break-words rounded-xl bg-brand-50 px-3 py-2 text-[0.875rem] leading-relaxed sm:max-w-[80%]">{pending.text}</div>
          </div>
        )}
        {busy && <ThinkingBubble seconds={seconds} label={seconds >= 20 ? "Still working — looking things up…" : "Thinking…"} />}
        {!busy && busyLabel && <ThinkingBubble seconds={busySeconds ?? 0} label={busyLabel} />}
        <div ref={endRef} />
      </div>

      <div className={clsx("shrink-0", compact ? "border-t border-[var(--line)] p-2.5" : "pt-2")}>
        {err && <p role="alert" className="mb-1.5 text-sm text-red-600">{err}</p>}
        {files.length > 0 && (
          <div className="mb-1.5 flex flex-wrap gap-1.5">
            {files.map((f, i) => (
              <span key={`${f.name}-${i}`} className="chip max-w-full">
                <FileText size={11} className="shrink-0" /> <span className="truncate">{f.name}</span>
                <button type="button" aria-label={`Remove ${f.name}`} className="ml-0.5 rounded px-0.5 hover:bg-[var(--panel-2)]" onClick={() => setFiles((l) => l.filter((_, j) => j !== i))}><X size={11} /></button>
              </span>
            ))}
          </div>
        )}
        <div className="flex items-end gap-1.5 rounded-xl border border-[var(--line-strong)] bg-[var(--panel)] px-1.5 py-1.5 focus-within:border-brand-600">
          <input ref={fileRef} type="file" multiple hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
          <button type="button" className="btn btn-ghost btn-icon !h-8 !min-h-0 !w-8 shrink-0" aria-label="Attach files" title={`Attach files (up to ${MAX_FILES}, 10 MB each)`} disabled={busy} onClick={() => fileRef.current?.click()}>
            <Paperclip size={16} />
          </button>
          <textarea
            ref={taRef}
            rows={1}
            value={input}
            aria-label="Message Fusion AI"
            placeholder="Ask Fusion AI…"
            className="max-h-40 min-w-0 flex-1 resize-none border-none bg-transparent px-1 py-1.5 text-[0.9rem] leading-snug outline-none"
            onChange={(e) => { setInput(e.target.value); e.target.style.height = "auto"; e.target.style.height = `${Math.min(160, e.target.scrollHeight)}px`; }}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); void send(); } }}
            onPaste={(e) => { if (e.clipboardData?.files?.length) { e.preventDefault(); addFiles(e.clipboardData.files); } }}
          />
          <button type="button" className="btn btn-primary btn-icon !h-8 !min-h-0 !w-8 shrink-0" aria-label="Send" disabled={busy || (!input.trim() && !files.length)} onClick={() => void send()}>
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
          </button>
        </div>
        {!compact && <p className="subtle mt-1 text-[11px]">Enter sends, Shift+Enter for a new line. Nothing changes until you confirm a card.</p>}
      </div>
    </div>
  );
}

function FileChips({ names }: { names: string[] }) {
  if (!names.length) return null;
  return (
    <div className="mb-1 flex flex-wrap justify-end gap-1">
      {names.map((n, i) => <span key={`${n}-${i}`} className="chip max-w-full"><FileText size={11} className="shrink-0" /><span className="truncate">{n}</span></span>)}
    </div>
  );
}

/** A long answer still being written (it carries on in the background). */
function PendingAnswer({ since }: { since: string }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const s = Math.max(0, Math.round((now - new Date(since).getTime()) / 1000));
  return <ThinkingBubble seconds={s} label={s < 60 ? "Writing…" : "Writing a long answer — you can leave this page, it keeps going…"} />;
}

/** The answer as a clean printable page; the browser's "Save as PDF" makes the PDF (tables and headings kept). */
function printAnswer(el: HTMLElement | null, title?: string) {
  if (!el) return;
  const w = window.open("", "_blank");
  if (!w) return;
  const body = el.cloneNode(true) as HTMLElement;
  body.querySelectorAll("[data-noprint], button").forEach((n) => n.remove());
  const esc = (t: string) => t.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" })[c]!);
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(title || document.title || "Fusion AI")}</title><style>
    body{font:11pt/1.5 -apple-system,Segoe UI,Calibri,sans-serif;color:#111;max-width:780px;margin:24px auto;padding:0 16px}
    h1,h2,h3{line-height:1.25;margin:1.2em 0 .4em} table{border-collapse:collapse;width:100%;margin:.6em 0} td,th{border:1px solid #bbb;padding:4px 6px;text-align:left;vertical-align:top}
    ul,ol{padding-left:1.4em} hr{border:0;border-top:1px solid #ccc} @page{margin:18mm}
  </style></head><body>${body.innerHTML}</body></html>`);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 300);
}

function MessageView({ m, quickReplies, onQuickReply }: { m: AsMessage; quickReplies?: string[]; onQuickReply: (text: string) => void }) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const time = new Date(m.createdAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  if (m.role === "user") {
    return (
      <div className="my-2.5 flex flex-col items-end">
        <FileChips names={(m.files ?? []).map((f) => f.name)} />
        <div className="max-w-[92%] whitespace-pre-wrap break-words rounded-xl bg-brand-50 px-3 py-2 text-[0.875rem] leading-relaxed sm:max-w-[80%]">{m.content}</div>
      </div>
    );
  }
  if (m.meta?.pending) return <PendingAnswer since={m.meta.startedAt ?? m.createdAt} />;
  const looked = lookedAt(m.steps);
  const long = m.content.length > 600;
  return (
    <div className="my-2.5">
      <div ref={bodyRef} className="max-w-full rounded-xl bg-[var(--panel-2)] px-3 py-2 text-[0.875rem] leading-relaxed ring-1 ring-[var(--line)]">
        {!!m.meta?.minutes && <div className="section-label mb-1">Minutes</div>}
        {m.meta?.rhythm && <div className="muted mb-1 text-[11px] font-semibold uppercase tracking-wide">{RHYTHM_LABEL[m.meta.rhythm] ?? "From Fusion AI"}</div>}
        <Markdown text={m.content} />
        <div data-noprint className="mt-1.5 flex flex-wrap items-center gap-1 text-[11px] text-[var(--muted)]">
          <span>{time}</span>
          {long && (
            <>
              <span aria-hidden>·</span>
              <a className="rounded-full bg-[var(--panel)] px-1.5 font-medium text-brand-700 ring-1 ring-[var(--line)] hover:underline" href={`/api/assistant/messages/${m.id}/export.docx`} title="Download this answer as a Word document">Word</a>
              <button type="button" className="rounded-full bg-[var(--panel)] px-1.5 font-medium text-brand-700 ring-1 ring-[var(--line)] hover:underline" onClick={() => printAnswer(bodyRef.current, m.meta?.title)} title="Open as a printable page — choose Save as PDF">PDF</button>
            </>
          )}
          {looked.length > 0 && (
            <>
              <span aria-hidden>·</span>
              <span>Looked at:</span>
              {looked.map((w) => <span key={w} className="rounded-full bg-[var(--panel)] px-1.5 ring-1 ring-[var(--line)]">{w}</span>)}
            </>
          )}
        </div>
      </div>
      {(m.actions ?? []).map((a, i) => {
        const meeting = m.actions?.[0]?.tool === "save_meeting";
        if (meeting && i > 0 && !m.applied?.["0"]?.dismissed) return null; // the meeting is saved as one note; the per-point cards only on "Review each"
        return <ActionCard key={i} message={m} idx={i} action={a} />;
      })}
      {Array.isArray(quickReplies) && quickReplies.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {quickReplies.filter((q) => typeof q === "string" && q.trim()).map((q) => (
            <button key={q} type="button" className="chip cursor-pointer whitespace-normal hover:border-brand-200" onClick={() => onQuickReply(q)}>{q}</button>
          ))}
        </div>
      )}
    </div>
  );
}

const TASK_TOOLS = new Set(["create_task", "update_task", "set_task_status", "add_comment", "chase"]);
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function taskLink(a: AsAction, applied: AsApplied): string | null {
  if (!TASK_TOOLS.has(a.tool)) return null;
  const r = applied.result as { item?: { id?: unknown }; id?: unknown } | undefined;
  const cand = r?.item?.id ?? r?.id ?? a.args.id;
  return typeof cand === "string" && UUID.test(cand) ? cand : null;
}

const show = (v: unknown): string => (Array.isArray(v) ? v.map(show).join(", ") : v && typeof v === "object" ? JSON.stringify(v) : String(v));

/** The few args worth showing on a generic card (ids are noise). */
function keyArgs(a: AsAction): Array<[string, string]> {
  return Object.entries(a.args ?? {})
    .filter(([k, v]) => v != null && v !== "" && k !== "id" && !(typeof v === "string" && UUID.test(v)))
    .slice(0, 5)
    .map(([k, v]) => [k.replace(/([A-Z])/g, " $1").replace(/_/g, " ").toLowerCase(), show(v).slice(0, 200)]);
}

function ActionCard({ message, idx, action }: { message: AsMessage; idx: number; action: AsAction }) {
  const qc = useQueryClient();
  const stored = message.applied?.[String(idx)];
  const [local, setLocal] = useState<AsApplied | null>(null);
  const state = local ?? stored ?? null;
  const [busy, setBusy] = useState<"" | "go" | "dismiss">("");
  const [err, setErr] = useState("");
  const isEmail = action.tool === "send_email" || action.tool === "reply_email";
  const [subject, setSubject] = useState(String(action.args.subject ?? ""));
  const [body, setBody] = useState(String(action.args.body ?? ""));
  const [line, setLine] = useState(String(action.args.line ?? ""));

  const done = state?.ok === true;
  const dismissed = state?.dismissed === true;
  const failed = state?.ok === false;
  const open = !done && !dismissed;

  const isMeeting = action.tool === "save_meeting";
  const meetTasks = isMeeting && Array.isArray(action.args.tasks) ? (action.args.tasks as string[]) : [];
  const meetNotes = isMeeting && Array.isArray(action.args.notes) ? (action.args.notes as string[]) : [];
  const run = async (dismiss: boolean, noteOnly = false) => {
    setBusy(dismiss ? "dismiss" : "go"); setErr("");
    let payload: Record<string, unknown> = {};
    if (dismiss) payload = { dismiss: true };
    else if (isMeeting && noteOnly) payload = { args: { tasks: [] } };
    else if (action.tool === "send_email") payload = { args: { subject, body } };
    else if (action.tool === "reply_email") payload = { args: { body, ...(action.args.subject !== undefined ? { subject } : {}) } };
    else if (action.tool === "create_task") payload = { args: { line } };
    try {
      const r = await call<{ ok?: boolean; applied?: AsApplied; error?: string }>("POST", `/api/assistant/messages/${message.id}/actions/${idx}`, payload);
      if (r.body?.applied) setLocal(r.body.applied);
      if (r.status === 409) {
        setErr("This card was already handled.");
        await qc.invalidateQueries({ queryKey: ["assistant", "conversation"] });
      } else if (!r.ok && !r.body?.applied) setErr(errText(r.body, r.status));
      if (r.ok && !dismiss) void refreshAfterAction(qc);
      if (isMeeting) void qc.invalidateQueries({ queryKey: ["assistant", "conversation"] }); // shows or hides the per-point cards
    } catch (e) { setErr((e as Error).message || "Could not reach ONE"); }
    setBusy("");
  };

  const link = done && state ? taskLink(action, state) : null;
  const fieldId = `act-${message.id}-${idx}`;
  return (
    <div className={clsx("mt-2 rounded-xl border bg-[var(--panel)] p-3 text-[0.84rem]", done ? "border-emerald-200" : failed ? "border-red-200" : "border-brand-200", dismissed && "opacity-55")}>
      <div className="flex items-start gap-2">
        {done ? <Check size={15} className="mt-0.5 shrink-0 text-emerald-600" /> : <Sparkles size={14} className="mt-0.5 shrink-0 text-brand-600" />}
        <div className="min-w-0 flex-1 break-words font-semibold">{action.summary}</div>
      </div>

      {open && isEmail && (
        <div className="mt-2 space-y-2">
          {Array.isArray(action.args.to) && <div className="text-xs"><span className="muted">To:</span> {show(action.args.to)}</div>}
          {Array.isArray(action.args.cc) && action.args.cc.length > 0 && <div className="text-xs"><span className="muted">Cc:</span> {show(action.args.cc)}</div>}
          {action.tool === "reply_email" && <div className="muted text-xs">{action.args.mode === "reply_all" ? "Reply to all" : "Reply"} in the same conversation</div>}
          {(action.tool === "send_email" || action.args.subject !== undefined) && (
            <label className="block text-xs" htmlFor={`${fieldId}-s`}>
              <span className="muted mb-0.5 block">Subject</span>
              <input id={`${fieldId}-s`} className="input !min-h-0 py-1.5 text-sm" value={subject} onChange={(e) => setSubject(e.target.value)} disabled={!!busy} />
            </label>
          )}
          <label className="block text-xs" htmlFor={`${fieldId}-b`}>
            <span className="muted mb-0.5 block">Message</span>
            <textarea id={`${fieldId}-b`} className="input min-h-32 py-1.5 text-sm leading-snug" value={body} onChange={(e) => setBody(e.target.value)} disabled={!!busy} />
          </label>
        </div>
      )}
      {open && action.tool === "create_task" && (
        <label className="mt-2 block text-xs" htmlFor={`${fieldId}-l`}>
          <span className="muted mb-0.5 block">Task line (dates, #company, @person and !high work as usual)</span>
          <input id={`${fieldId}-l`} className="input !min-h-0 py-1.5 text-sm" value={line} onChange={(e) => setLine(e.target.value)} disabled={!!busy} />
        </label>
      )}
      {open && isMeeting && (
        <div className="mt-2 space-y-1 rounded-lg bg-[var(--panel-2)] px-3 py-2 text-xs">
          <div className="font-semibold">{String(action.args.title ?? "Meeting")}</div>
          <div className="muted">One note: the minutes above{meetNotes.length ? `, ${meetNotes.length} note point${meetNotes.length === 1 ? "" : "s"} as sub-points` : ""}, decisions and open questions — folder Meetings.</div>
          {meetTasks.length > 0 && (
            <div>
              <div className="muted">Tasks, linked to the note:</div>
              <ul className="ml-4 list-disc">{meetTasks.map((t) => <li key={t} className="break-words">{t}</li>)}</ul>
            </div>
          )}
        </div>
      )}
      {open && !isMeeting && !isEmail && action.tool !== "create_task" && keyArgs(action).length > 0 && (
        <dl className="mt-1.5 grid grid-cols-[auto_minmax(0,1fr)] gap-x-2 gap-y-0.5 text-xs">
          {keyArgs(action).map(([k, v]) => <div key={k} className="contents"><dt className="muted">{k}</dt><dd className="break-words">{v}</dd></div>)}
        </dl>
      )}

      {failed && state?.error && <p role="alert" className="mt-2 text-xs text-red-700">Didn't work: {state.error}</p>}
      {err && <p role="alert" className="mt-2 text-xs text-red-700">{err}</p>}

      <div className="mt-2.5 flex flex-wrap items-center gap-2">
        {done && (
          <>
            <span className="text-xs font-semibold text-emerald-700">Done</span>
            {link && <Link to="/work/$id" params={{ id: link }} className="inline-flex items-center gap-1 text-xs text-brand-600 hover:underline">Open task <ExternalLink size={11} /></Link>}
          </>
        )}
        {dismissed && <span className="muted text-xs">Not now</span>}
        {open && isMeeting && (
          <>
            <button type="button" className="btn btn-primary btn-sm" disabled={!!busy} onClick={() => void run(false)}>
              {busy === "go" ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
              {failed ? "Try again" : meetTasks.length ? `Save note + ${meetTasks.length} task${meetTasks.length === 1 ? "" : "s"}` : "Save note"}
            </button>
            {meetTasks.length > 0 && <button type="button" className="btn btn-sm" disabled={!!busy} onClick={() => void run(false, true)}>Note only</button>}
            <button type="button" className="btn btn-sm" disabled={!!busy} onClick={() => void run(true)}>Review each</button>
          </>
        )}
        {done && isMeeting && (() => {
          const noteId = (state?.result as { id?: unknown } | undefined)?.id ?? (state as { id?: unknown } | null)?.id;
          return typeof noteId === "string" ? <a href={`/notes?note=${noteId}`} className="inline-flex items-center gap-1 text-xs text-brand-600 hover:underline">Open note <ExternalLink size={11} /></a> : null;
        })()}
        {open && !isMeeting && (
          <>
            <button type="button" className="btn btn-primary btn-sm" disabled={!!busy || (action.tool === "create_task" && !line.trim()) || (isEmail && !body.trim())} onClick={() => void run(false)}>
              {busy === "go" ? <Loader2 size={13} className="animate-spin" /> : isEmail ? <Send size={13} /> : <Check size={13} />}
              {failed ? (isEmail ? "Try sending again" : "Try again") : isEmail ? "Send" : "Confirm"}
            </button>
            <button type="button" className="btn btn-sm" disabled={!!busy} onClick={() => void run(true)}>Not now</button>
            {isEmail && <span className="muted text-[11px]">Sending can't be undone.</span>}
          </>
        )}
      </div>
    </div>
  );
}
